import type { Appearance, CssFontSource } from "@stripe/stripe-js";
import { fontFamilyFor, fontStylesheet, resolvedTokens } from "./theme";
import type { BrandingTokens } from "./theme";

/**
 * Appearance for the drop-in component.
 *
 * Two inputs, one answer. The organizer's stored branding is the default —
 * an organizer who never integrates still gets their own checkout — and the
 * appearance a partner passes at mount overrides it part by part. The result
 * is expressed once, as CSS custom properties on the component root, and is
 * translated for both Stripe Elements instances so the card fields belong to
 * the same design as everything around them.
 *
 * The identity pieces (`@libs/identity-ui`: the email chip, the six-slot
 * code, the mark) read a short token contract of their own; the partner's
 * appearance is mapped onto it below, once. Only the recognised buyer's chip
 * (the app icon's ground and border) and the mark keep Lissen's own colours.
 */

export type AppearanceColors = {
  /** The component's own backdrop. */
  background?: string;
  /** Cards, panels and fields sitting on the backdrop. */
  surface?: string;
  text?: string;
  muted?: string;
  accent?: string;
  /** Text and icons on top of `accent`. */
  onAccent?: string;
  danger?: string;
  border?: string;
};

export type ButtonAppearance = {
  background?: string;
  text?: string;
  border?: string;
  radius?: string | number;
  padding?: string;
  fontWeight?: string | number;
  shadow?: string;
};

export type InputAppearance = {
  background?: string;
  text?: string;
  placeholder?: string;
  border?: string;
  radius?: string | number;
  padding?: string;
  focusBorder?: string;
  focusShadow?: string;
};

/** The four parts a partner may address on their own. */
export type CheckoutPart = "cart" | "contact" | "payment" | "terminal";

export type PartAppearance = {
  background?: string;
  text?: string;
  border?: string;
  radius?: string | number;
  padding?: string;
  gap?: string | number;
  shadow?: string;
};

export type CheckoutAppearance = {
  colors?: AppearanceColors;
  fontFamily?: string;
  fontSize?: string | number;
  /** Base corner radius; `radiusSmall`/`radiusLarge` default from it. */
  radius?: string | number;
  radiusSmall?: string | number;
  radiusLarge?: string | number;
  /** One step of the spacing scale. Every gap is a multiple of it. */
  spacing?: string | number;
  borderWidth?: string | number;
  borderStyle?: string;
  shadows?: { card?: string; focus?: string };
  buttons?: { primary?: ButtonAppearance; secondary?: ButtonAppearance };
  input?: InputAppearance;
  parts?: Partial<Record<CheckoutPart, PartAppearance>>;
  stripe?: {
    theme?: "stripe" | "night" | "flat";
    labels?: "above" | "floating";
    /** Stripe's colour variables refuse translucent values. When `surface`
     *  or `input.background` is one, this opaque colour is what Stripe is
     *  told sits under the card fields; the translucent value still paints
     *  them. Ignored unless it is a plain opaque hex colour. */
    background?: string;
  };
};

/**
 * Nothing a partner sends becomes CSS unchecked. A custom property is set
 * through the CSSOM, so a malformed value is dropped by the browser rather
 * than escaping its declaration — but a value is still read by a human in
 * devtools and copied into a stylesheet one day, so the obvious ways to
 * smuggle a second declaration, a request or a script are refused outright.
 */
const FORBIDDEN = /[;{}<>\\]|url\s*\(|expression\s*\(|@import|javascript:/i;

export function cssValue(value: unknown): string | undefined {
  if (typeof value === "number")
    return Number.isFinite(value) ? `${value}px` : undefined;
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 200 || FORBIDDEN.test(trimmed))
    return undefined;
  return trimmed;
}

/** A font stack may name families and generics; it may not do anything else. */
export function cssFontFamily(value: unknown): string | undefined {
  const family = cssValue(value);
  return family && /^[-\w\s,'"().]+$/.test(family) ? family : undefined;
}

type Scale = { base: string; small: string; large: string };

export type ResolvedAppearance = {
  colors: Required<AppearanceColors>;
  fontFamily: string;
  fontSize: string;
  radius: Scale;
  spacing: string;
  borderWidth: string;
  borderStyle: string;
  shadows: { card: string; focus: string };
  buttons: {
    primary: Required<Omit<ButtonAppearance, "radius" | "fontWeight">> & {
      radius: string;
      fontWeight: string;
    };
    secondary: Required<Omit<ButtonAppearance, "radius" | "fontWeight">> & {
      radius: string;
      fontWeight: string;
    };
  };
  input: Required<Omit<InputAppearance, "radius">> & { radius: string };
  parts: Record<CheckoutPart, PartAppearance>;
  stripe: {
    theme: "stripe" | "night" | "flat";
    labels: "above" | "floating";
    background: string | null;
  };
  /** The webfont the organizer's `font_key` needs, if any. */
  fontStylesheetUrl: string | null;
};

const STRIPE_THEMES = ["stripe", "night", "flat"] as const;
const STRIPE_LABELS = ["above", "floating"] as const;
/** What Stripe's colour variables accept from a partner: an opaque hex. */
const OPAQUE_HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/** `parseAppearance` only proves the appearance is an object — every value
 *  inside it is the partner page's, unchecked. These two are handed straight
 *  to Stripe Elements, so they are narrowed to what Elements declares rather
 *  than passed through. */
const literal = <T extends string>(
  allowed: ReadonlyArray<T>,
  value: unknown,
  fallback: T,
): T =>
  typeof value === "string" &&
  (allowed as ReadonlyArray<string>).includes(value)
    ? (value as T)
    : fallback;

function scale(
  input: CheckoutAppearance | undefined,
  fallbackRadius: string,
): Scale {
  const base = cssValue(input?.radius) ?? fallbackRadius;
  return {
    base,
    small: cssValue(input?.radiusSmall) ?? `calc(${base} * 0.6)`,
    large: cssValue(input?.radiusLarge) ?? `calc(${base} * 1.6)`,
  };
}

function part(input: PartAppearance | undefined): PartAppearance {
  if (!input) return {};
  return {
    background: cssValue(input.background),
    text: cssValue(input.text),
    border: cssValue(input.border),
    radius: cssValue(input.radius),
    padding: cssValue(input.padding),
    gap: cssValue(input.gap),
    shadow: cssValue(input.shadow),
  };
}

/**
 * Branding first, then the partner's overrides, then Lissen's neutral
 * defaults for anything neither supplied.
 */
export function resolveAppearance(
  tokens: BrandingTokens,
  input?: CheckoutAppearance,
): ResolvedAppearance {
  const branding = resolvedTokens(tokens);
  const colors = input?.colors ?? {};
  const background = cssValue(colors.background) ?? branding.background_color;
  const surface = cssValue(colors.surface) ?? background;
  const text = cssValue(colors.text) ?? branding.text_color;
  const muted = cssValue(colors.muted) ?? branding.accent_color;
  const accent = cssValue(colors.accent) ?? branding.brand_color;
  const onAccent = cssValue(colors.onAccent) ?? readableOn(accent);
  const danger = cssValue(colors.danger) ?? "#b91c1c";
  const border =
    cssValue(colors.border) ?? `color-mix(in srgb, ${text} 16%, transparent)`;
  const radius = scale(input, `${branding.corner_radius}px`);
  const spacing = cssValue(input?.spacing) ?? "8px";
  const borderWidth = cssValue(input?.borderWidth) ?? "1px";
  const borderStyle = cssValue(input?.borderStyle) ?? "solid";
  const fontFamily =
    cssFontFamily(input?.fontFamily) ?? fontFamilyFor(branding.font_key);
  const primary = input?.buttons?.primary ?? {};
  const secondary = input?.buttons?.secondary ?? {};
  const field = input?.input ?? {};

  return {
    colors: {
      background,
      surface,
      text,
      muted,
      accent,
      onAccent,
      danger,
      border,
    },
    fontFamily,
    fontSize: cssValue(input?.fontSize) ?? "16px",
    radius,
    spacing,
    borderWidth,
    borderStyle,
    shadows: {
      card:
        cssValue(input?.shadows?.card) ??
        `0 18px 50px color-mix(in srgb, ${text} 8%, transparent)`,
      focus:
        cssValue(input?.shadows?.focus) ??
        `0 0 0 3px color-mix(in srgb, ${accent} 30%, transparent)`,
    },
    buttons: {
      primary: {
        background: cssValue(primary.background) ?? accent,
        text: cssValue(primary.text) ?? onAccent,
        border: cssValue(primary.border) ?? "transparent",
        radius: cssValue(primary.radius) ?? radius.base,
        padding:
          cssValue(primary.padding) ??
          `calc(${spacing} * 1.5) calc(${spacing} * 2.25)`,
        fontWeight: cssValue(primary.fontWeight) ?? "650",
        shadow: cssValue(primary.shadow) ?? "none",
      },
      secondary: {
        background: cssValue(secondary.background) ?? "transparent",
        text: cssValue(secondary.text) ?? text,
        border: cssValue(secondary.border) ?? muted,
        radius: cssValue(secondary.radius) ?? radius.base,
        padding:
          cssValue(secondary.padding) ??
          `calc(${spacing} * 1.25) calc(${spacing} * 1.75)`,
        fontWeight: cssValue(secondary.fontWeight) ?? "550",
        shadow: cssValue(secondary.shadow) ?? "none",
      },
    },
    input: {
      background: cssValue(field.background) ?? surface,
      text: cssValue(field.text) ?? text,
      placeholder: cssValue(field.placeholder) ?? muted,
      border: cssValue(field.border) ?? border,
      radius: cssValue(field.radius) ?? radius.base,
      padding:
        cssValue(field.padding) ??
        `calc(${spacing} * 1.375) calc(${spacing} * 1.5)`,
      focusBorder: cssValue(field.focusBorder) ?? accent,
      focusShadow:
        cssValue(field.focusShadow) ??
        `0 0 0 3px color-mix(in srgb, ${accent} 25%, transparent)`,
    },
    parts: {
      cart: part(input?.parts?.cart),
      contact: part(input?.parts?.contact),
      payment: part(input?.parts?.payment),
      terminal: part(input?.parts?.terminal),
    },
    stripe: {
      theme: literal(STRIPE_THEMES, input?.stripe?.theme, "stripe"),
      labels: literal(STRIPE_LABELS, input?.stripe?.labels, "above"),
      background:
        typeof input?.stripe?.background === "string" &&
        OPAQUE_HEX.test(input.stripe.background.trim())
          ? input.stripe.background.trim()
          : null,
    },
    fontStylesheetUrl: cssFontFamily(input?.fontFamily)
      ? null
      : fontStylesheet(tokens),
  };
}

const PART_PREFIX: Record<CheckoutPart, string> = {
  cart: "--lc-cart",
  contact: "--lc-contact",
  payment: "--lc-payment",
  terminal: "--lc-terminal",
};

/** The whole answer as custom properties for the component root. */
export function appearanceVariables(
  resolved: ResolvedAppearance,
): Record<string, string> {
  const variables: Record<string, string> = {
    "--lc-background": resolved.colors.background,
    "--lc-surface": resolved.colors.surface,
    "--lc-text": resolved.colors.text,
    "--lc-muted": resolved.colors.muted,
    "--lc-accent": resolved.colors.accent,
    "--lc-on-accent": resolved.colors.onAccent,
    "--lc-danger": resolved.colors.danger,
    "--lc-border-color": resolved.colors.border,
    "--lc-font-family": resolved.fontFamily,
    "--lc-font-size": resolved.fontSize,
    "--lc-input-font-size": inputFontSize(resolved.fontSize),
    "--lc-radius": resolved.radius.base,
    "--lc-radius-sm": resolved.radius.small,
    "--lc-radius-lg": resolved.radius.large,
    "--lc-space": resolved.spacing,
    "--lc-border-width": resolved.borderWidth,
    "--lc-border-style": resolved.borderStyle,
    "--lc-border": `${resolved.borderWidth} ${resolved.borderStyle} ${resolved.colors.border}`,
    "--lc-shadow-card": resolved.shadows.card,
    "--lc-shadow-focus": resolved.shadows.focus,
    "--lc-button-background": resolved.buttons.primary.background,
    "--lc-button-text": resolved.buttons.primary.text,
    "--lc-button-border": resolved.buttons.primary.border,
    "--lc-button-radius": resolved.buttons.primary.radius,
    "--lc-button-padding": resolved.buttons.primary.padding,
    "--lc-button-weight": resolved.buttons.primary.fontWeight,
    "--lc-button-shadow": resolved.buttons.primary.shadow,
    "--lc-button2-background": resolved.buttons.secondary.background,
    "--lc-button2-text": resolved.buttons.secondary.text,
    "--lc-button2-border": resolved.buttons.secondary.border,
    "--lc-button2-radius": resolved.buttons.secondary.radius,
    "--lc-button2-padding": resolved.buttons.secondary.padding,
    "--lc-button2-weight": resolved.buttons.secondary.fontWeight,
    "--lc-button2-shadow": resolved.buttons.secondary.shadow,
    "--lc-input-background": resolved.input.background,
    "--lc-input-text": resolved.input.text,
    "--lc-input-placeholder": resolved.input.placeholder,
    "--lc-input-height": "44px",
    "--lc-input-border": resolved.input.border,
    "--lc-input-radius": resolved.input.radius,
    "--lc-input-padding": resolved.input.padding,
    "--lc-input-focus-border": resolved.input.focusBorder,
    "--lc-input-focus-shadow": resolved.input.focusShadow,
    // The identity chrome's only inputs from the partner: what reads on
    // their surface. Everything else about it is Lissen's and fixed.
    "--lissen-on-surface": resolved.colors.text,
    "--lissen-on-surface-muted": resolved.colors.muted,
    // The shared identity pieces (@libs/identity-ui) read their own short
    // contract; the partner's appearance is mapped onto it here, once.
    "--lid-text": resolved.colors.text,
    "--lid-muted": resolved.colors.muted,
    "--lid-surface": resolved.input.background,
    "--lid-border": resolved.input.border,
    "--lid-border-width": resolved.borderWidth,
    "--lid-accent": resolved.input.focusBorder,
    "--lid-focus-ring": resolved.input.focusShadow,
    "--lid-danger": resolved.colors.danger,
    "--lid-radius": resolved.input.radius,
    "--lid-radius-sm": resolved.radius.small,
    "--lid-control-height": "44px",
    "--lid-space": resolved.spacing,
    "--lid-font-size-small": "0.875em",
  };
  for (const [name, prefix] of Object.entries(PART_PREFIX)) {
    const overrides = resolved.parts[name as CheckoutPart];
    for (const [key, value] of Object.entries(overrides)) {
      if (typeof value === "string" && value)
        variables[`${prefix}-${key.toLowerCase()}`] = value;
    }
  }
  return variables;
}

export type StripeAppearance = {
  appearance: Appearance;
  fonts: Array<CssFontSource>;
};

/**
 * The same answer again, in Stripe's vocabulary, for both Elements
 * instances — the connected-account PaymentIntent and the platform
 * SetupIntent — so a saved card and a new one are entered into the same
 * looking field.
 */
export function stripeAppearance(
  resolved: ResolvedAppearance,
): StripeAppearance {
  return {
    appearance: {
      theme: resolved.stripe.theme,
      labels: resolved.stripe.labels,
      variables: {
        colorPrimary: resolved.colors.accent,
        // Stripe refuses a translucent colour here; the real field
        // background is set as a rule below, where any CSS colour is fine.
        colorBackground:
          resolved.stripe.background ?? resolved.input.background,
        colorText: resolved.colors.text,
        colorTextSecondary: resolved.colors.muted,
        colorDanger: resolved.colors.danger,
        borderRadius: resolved.input.radius,
        fontFamily: resolved.fontFamily,
        fontSizeBase: resolved.fontSize,
        // Stripe multiplies its own paddings from this; with floating
        // labels our 8px unit made each field a third taller than ours.
        spacingUnit:
          resolved.stripe.labels === "floating" ? "3px" : resolved.spacing,
      },
      rules: {
        ".Input": {
          backgroundColor: resolved.input.background,
          color: resolved.input.text,
          fontSize: inputFontSize(resolved.fontSize),
          border: `${resolved.borderWidth} ${resolved.borderStyle} ${resolved.input.border}`,
          // Floating labels: tighter padding so the card fields stand as
          // tall as the component's own 44px controls, not Stripe's default.
          padding:
            resolved.stripe.labels === "floating"
              ? `4px calc(${resolved.spacing} * 1.5)`
              : resolved.input.padding,
          boxShadow: "none",
        },
        ".Input::placeholder": { color: resolved.input.placeholder },
        ".Label--floating": { fontSize: "11px", lineHeight: "1.2" },
        ".Input:focus": {
          border: `${resolved.borderWidth} ${resolved.borderStyle} ${resolved.input.focusBorder}`,
          boxShadow: resolved.input.focusShadow,
        },
        ".Label": { color: resolved.colors.muted },
      },
    },
    fonts: resolved.fontStylesheetUrl
      ? [{ cssSrc: resolved.fontStylesheetUrl }]
      : [],
  };
}

/**
 * The font size a text input is given: the component's own size, floored at
 * 16px. iOS Safari (and every WebKit browser on iOS) zooms the page into any
 * focused field whose text is smaller than 16px, and Chrome on Android does
 * the same at small sizes — the buyer taps the email field and the checkout
 * lurches. A size that cannot be read as pixels is passed through unchanged.
 */
export function inputFontSize(fontSize: string): string {
  const px = /^\s*(\d+(?:\.\d+)?)px\s*$/.exec(fontSize);
  if (!px) return fontSize;
  return Number(px[1]) < 16 ? "16px" : fontSize;
}

/** Black or white, whichever a buyer can read on the given colour. Unknown
 *  colour spaces fall back to white, which is what an accent normally wants. */
export function readableOn(color: string): string {
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
  if (!hex) return "#ffffff";
  const value =
    hex[1].length === 3
      ? hex[1]
          .split("")
          .map((c) => c + c)
          .join("")
      : hex[1];
  const [r, g, b] = [0, 2, 4].map(
    (i) => parseInt(value.slice(i, i + 2), 16) / 255,
  );
  const channel = (c: number) =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  const luminance =
    0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  return luminance > 0.45 ? "#111827" : "#ffffff";
}

/** A partner's appearance as it arrives over postMessage: unknown until
 *  proved otherwise, and never trusted to be the shape it claims. */
export function parseAppearance(
  value: unknown,
): CheckoutAppearance | undefined {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value
    : undefined;
}
